import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { CopyOperationMovementMappings } from "@/components/copy-operations/CopyOperationMovementMappings";
import { CopyOperationStagingEditor } from "@/components/copy-operations/CopyOperationStagingEditor";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { StatusBadge } from "@/components/shared/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useCommitCopyOperation,
  useCopyOperation,
  useDeleteCopyOperation,
  useUpdateCopyOperationStaging,
} from "@/hooks/use-copy-operations";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useSession } from "@/lib/auth-client";
import type { Contract } from "@/lib/coach-manager/types";

const routeApi = getRouteApi("/dashboard/copy-operations/$id");

export function CopyOperationDetailPage() {
  const { id } = routeApi.useParams();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const isAdmin = session?.user?.role === "admin";
  const { data, isLoading, isError, error, refetch } = useCopyOperation(id);
  const [contract, setContract] = useState<Contract | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [acknowledgedUnresolved, setAcknowledgedUnresolved] = useState(false);
  const initializedFor = useRef<string | null>(null);

  const updateStaging = useUpdateCopyOperationStaging(id);
  const commit = useCommitCopyOperation();
  const deleteOperation = useDeleteCopyOperation();

  const operation = data?.operation;
  const editable = operation?.status === "pending" || operation?.status === "failed";

  // Load the staged payload into local editable state once per operation —
  // never re-synced afterward (autosave is fire-and-forget, not refetched),
  // so an in-flight edit never gets clobbered by a stray refetch.
  useEffect(() => {
    if (operation && initializedFor.current !== operation.id) {
      initializedFor.current = operation.id;
      setContract(operation.stagedPayload);
      // Ephemeral, client-side only — a reload or revisit requires
      // re-acknowledging the unresolved refs, matching the "never a silent
      // default" policy. Never persisted to the operation row.
      setAcknowledgedUnresolved(false);
    }
  }, [operation]);

  const debouncedContract = useDebouncedValue(contract, 800);
  const lastSavedRef = useRef<Contract | null>(null);
  useEffect(() => {
    if (!debouncedContract || !editable) return;
    if (lastSavedRef.current === debouncedContract) return;
    lastSavedRef.current = debouncedContract;
    updateStaging.mutate(
      { stagedPayload: debouncedContract },
      {
        onSuccess: (result) => {
          setSaveError(result.ok ? null : result.error);
        },
      },
    );
  }, [debouncedContract, editable, updateStaging]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !data || !operation || !contract) {
    return <ErrorState message={error?.message ?? "Copy operation not found"} onRetry={refetch} />;
  }

  const targetBuId = operation.targetBuId;

  function handleCommit() {
    commit.mutate(id);
  }

  function handleDeleteConfirm() {
    deleteOperation.mutate(id, {
      onSuccess: () => navigate({ to: "/dashboard/copy-operations" }),
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Copy: Contract #${operation.sourceObjectId}`}
        description={`${data.sourceBuName ?? operation.sourceBuId} → ${data.targetBuName ?? operation.targetBuId} (Client ${operation.targetCustomerId})`}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={operation.status} />
            {operation.resultObjectId && (
              <Badge variant="success">New Contract #{operation.resultObjectId}</Badge>
            )}
          </div>
        }
      />

      {saveError && <ErrorState message={`Autosave failed: ${saveError}`} />}

      {commit.data && !commit.data.ok && <ErrorState message={commit.data.error} />}
      {commit.error && <ErrorState message={commit.error.message} />}
      {!commit.data &&
        !commit.error &&
        operation.status === "failed" &&
        operation.failureReason && <ErrorState message={operation.failureReason} />}

      {operation.status === "completed" && (
        <>
          <EmptyState
            title="Copy complete"
            description={`Posted successfully as Contract #${operation.resultObjectId}.`}
          />
          <CopyOperationMovementMappings
            movements={operation.sourceSnapshot.ContractMovements ?? []}
            mappings={data.movementMappings}
          />
        </>
      )}

      <CopyOperationStagingEditor
        contract={contract}
        targetBuId={targetBuId}
        unresolvedRefs={operation.unresolvedRefs}
        disabled={!editable || commit.isPending}
        showUnresolvedPanel={editable}
        onChange={setContract}
        acknowledgedUnresolved={acknowledgedUnresolved}
        onAcknowledgedUnresolvedChange={setAcknowledgedUnresolved}
      />

      {editable && (
        <div className="flex justify-end gap-2">
          {isAdmin && (
            <Button
              type="button"
              variant="outline"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeleteConfirmOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => setPreviewOpen(true)}>
            Preview JSON
          </Button>
          <Button
            type="button"
            onClick={handleCommit}
            disabled={
              commit.isPending || (operation.unresolvedRefs.length > 0 && !acknowledgedUnresolved)
            }
          >
            {commit.isPending
              ? "Posting…"
              : operation.status === "failed"
                ? "Retry Post"
                : "Post to Target BU"}
          </Button>
        </div>
      )}

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Transformed payload</DialogTitle>
            <DialogDescription>
              The exact Contract that will be sent to the target BU if you post now.
            </DialogDescription>
          </DialogHeader>
          <pre className="max-h-[60vh] overflow-auto rounded-lg bg-muted p-4 text-xs">
            {JSON.stringify(contract, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>

      {isAdmin && (
        <ConfirmDialog
          open={deleteConfirmOpen}
          onOpenChange={setDeleteConfirmOpen}
          title="Delete copy operation"
          description={`Delete this draft copy of Contract #${operation.sourceObjectId}? This cannot be undone.`}
          confirmLabel="Delete"
          variant="destructive"
          loading={deleteOperation.isPending}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
}
